/**
 * A badge, drawn as an SVG medallion sized `size-badge` (docs/Decisions.md
 * D42). An org-designed badge's stripes become concentric rings, outermost
 * first; a platform status badge (src/shared/utils/badges.js) has no design
 * and is drawn on the brand colours with its initial. Same colour rules as
 * RibbonBar: token names map to CSS variables, org hex colours are content.
 */
import { cn } from '../../lib/ui';
import { colorFill, ribbonLabel, ribbonTooltip } from '../../lib/ribbons';
import { RibbonDevice } from './RibbonBar';

const R = 32;

function rings(stripes) {
  const list = Array.isArray(stripes) && stripes.length ? stripes : [];
  const total = list.reduce((sum, s) => sum + (s.width > 0 ? s.width : 1), 0);
  let outer = R;
  const inner = R * 0.42;
  return list.map(stripe => {
    const r = outer;
    outer -= ((stripe.width > 0 ? stripe.width : 1) / total) * (R - inner);
    return { r, fill: colorFill(stripe.color) };
  });
}

export default function BadgeMedallion({ item, showLevel = true, showScope = false, className }) {
  const label = showLevel ? ribbonLabel(item) : item.name;
  const tooltip = ribbonTooltip(showLevel ? item : { ...item, level: null }, { showScope });
  const designed = item.design && Array.isArray(item.design.stripes) && item.design.stripes.length;
  const initial = (item.name || '?').trim().charAt(0).toUpperCase();
  return (
    <span
      className={cn('inline-block size-badge shrink-0', item.hidden && 'opacity-55', className)}
      title={tooltip}
    >
      <svg role="img" aria-label={label} viewBox="0 0 64 64" className="block size-full">
        <title>{tooltip}</title>
        {designed ? (
          <>
            {rings(item.design.stripes).map((ring, index) => (
              <circle key={index} cx={R} cy={R} r={ring.r} fill={ring.fill} />
            ))}
            <circle cx={R} cy={R} r={R * 0.42} fill="var(--surface)" />
            {showLevel && (
              <g transform="translate(10 25)">
                <RibbonDevice
                  device={item.design.device}
                  level={item.level}
                  width={44}
                  height={14}
                />
              </g>
            )}
          </>
        ) : (
          <>
            <circle cx={R} cy={R} r={R} fill="var(--logo-blue)" />
            <circle cx={R} cy={R} r={R * 0.8} fill="var(--deep)" />
            <text
              x={R}
              y={R}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="26"
              fontWeight="700"
              fill="var(--on-deep)"
            >
              {initial}
            </text>
          </>
        )}
      </svg>
    </span>
  );
}
